import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAIAssistant } from '@/contexts/ai-assistant-context';
import { cn } from '@/lib/utils';

interface AIAssistantInputProps {
  readonly autoFocus?: boolean;
}

export function AIAssistantInput({ autoFocus = false }: AIAssistantInputProps): React.ReactElement {
  const [input, setInput] = useState('');
  const { sendMessage, isProcessing } = useAIAssistant();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const handleSubmit = async (): Promise<void> => {
    if (!input.trim() || isProcessing) return;

    const message = input.trim();
    setInput('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    await sendMessage(message);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setInput(e.target.value);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={isProcessing ? 'Type your next message...' : 'Type a message...'}
            className={cn(
              'min-h-[44px] max-h-[120px] resize-none',
              'focus-visible:ring-1',
            )}
            rows={1}
          />
        </div>
        <Button
          onClick={handleSubmit}
          disabled={!input.trim() || isProcessing}
          size="icon"
          className="h-11 w-11 flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Press <kbd className="px-1 py-0.5 text-xs bg-muted rounded">Enter</kbd> to send, <kbd className="px-1 py-0.5 text-xs bg-muted rounded">Shift+Enter</kbd> for new line
      </p>
    </div>
  );
}
